import { Controller } from "@hotwired/stimulus"

const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
const LEAFLET_JS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"

export default class extends Controller {
  static values = {
    markers: Array
  }

  async connect() {
    await this.loadLeaflet()
    this.initializeMap()
  }

  disconnect() {
    this.map?.remove()
    this.map = null
  }

  initializeMap() {
    if (this.map || !this.hasMarkersValue || this.markersValue.length === 0) return

    this.map = window.L.map(this.element)

    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(this.map)

    const bounds = []

    this.markersValue.forEach((markerData) => {
      const marker = window.L.marker(
        [markerData.latitude, markerData.longitude],
        this.markerOptions(markerData)
      ).addTo(this.map)
      marker.bindPopup(markerData.full_name)
      marker.on("mouseover", () => marker.openPopup())
      marker.on("mouseout", () => marker.closePopup())
      marker.on("click", () => {
        window.location.href = markerData.admin_url
      })
      bounds.push([markerData.latitude, markerData.longitude])
    })

    if (bounds.length === 1) {
      this.map.setView(bounds[0], 10)
    } else {
      this.map.fitBounds(bounds, { padding: [20, 20] })
    }
  }

  markerOptions(markerData) {
    const markerColor = markerData.markerColor || markerData.marker_color
    if (!markerColor) return {}

    return {
      icon: window.L.icon({
        iconUrl: this.markerIconUrl(markerColor),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        shadowSize: [41, 41],
        shadowAnchor: [12, 41]
      })
    }
  }

  markerIconUrl(color) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5 0 22 12.5 41 12.5 41S25 22 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="5" fill="#fff"/></svg>`
    return `data:image/svg+xml;base64,${window.btoa(svg)}`
  }

  loadLeaflet() {
    if (window.L) return Promise.resolve()
    if (window.providersMapLeafletPromise) return window.providersMapLeafletPromise

    this.ensureLeafletStylesheet()

    window.providersMapLeafletPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector("script[data-leaflet]")

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true })
        existingScript.addEventListener("error", reject, { once: true })
        return
      }

      const script = document.createElement("script")
      script.src = LEAFLET_JS_URL
      script.dataset.leaflet = "true"
      script.async = true
      script.addEventListener("load", () => resolve(), { once: true })
      script.addEventListener("error", reject, { once: true })
      document.head.appendChild(script)
    })

    return window.providersMapLeafletPromise
  }

  ensureLeafletStylesheet() {
    if (document.querySelector("link[data-leaflet]")) return

    const stylesheet = document.createElement("link")
    stylesheet.rel = "stylesheet"
    stylesheet.href = LEAFLET_CSS_URL
    stylesheet.dataset.leaflet = "true"
    document.head.appendChild(stylesheet)
  }
}
